<script lang="ts">
import { computed, inject, useSlots } from 'vue';

import { PhxsgIconProps, PhxsgIcon as PhxsgCoreIcon } from '@sg-core/src/View/Components/Atoms/Icon';
import { PhxsgLoaderProps, PhxsgLoader } from '@sg-core/src/View/Components/Atoms/Loader';

export type PhxsgButtonType = 'button' | 'a';

type PhxsgButtonIconProp =
  | {
      start?: PhxsgIconProps;
      end?: PhxsgIconProps;
    }
  | PhxsgIconProps;

export interface PhxsgButtonProps {
  use?: PhxsgButtonType;
  variant?: string;
  size?: string;
  icon?: PhxsgButtonIconProp;
  iconOnly?: boolean;
  justify?: 'end' | 'start' | 'between';
  full?: boolean;
  disabled?: boolean;
  label?: string;
  loading?: boolean;
  loaderProps?: PhxsgLoaderProps;
  bare?: boolean;
  href?: string | undefined;
  type?: 'button' | 'submit' | 'reset' | undefined;
  expandTarget?: boolean;
}
</script>

<script setup lang="ts">
// Allow maisons to override the icon component via provide/inject
const PhxsgIcon = inject('PhxsgIcon', PhxsgCoreIcon);

const {
  use = 'button',
  variant = null,
  size = null,
  icon = null,
  iconOnly = false,
  justify = null,
  full = false,
  disabled = false,
  label = null,
  loading = false,
  loaderProps = null,
  bare = false,
  href = undefined,
  type = 'button',
  expandTarget = false,
} = defineProps<PhxsgButtonProps>();

const slots = useSlots();

const hasDefaultSlot = computed(() => {
  const defaultSlot = slots.default?.();
  return Array.isArray(defaultSlot) && defaultSlot[0]?.children?.length;
});

const elementAttrs = computed(() => {
  if (use === 'button') {
    return {
      disabled: disabled || loading,
      type: type,
    };
  }

  if (use === 'a') {
    return {
      href,
      ...(disabled && {
        'aria-disabled': 'true',
        tabindex: -1,
      }),
    };
  }

  return {};
});

const startIcon = computed(() => (icon && 'start' in icon ? icon.start : null));
const endIcon = computed(() => (icon && 'end' in icon ? icon.end : null));
const onlyIcon = computed(() => icon && !('start' in icon || 'end' in icon) && (icon as PhxsgIconProps));

const rootClasses = computed(() => {
  if (bare) {
    return [{ 'phxsg-btn--expand-target': expandTarget }];
  }

  return [
    'phxsg-btn',
    {
      [`phxsg-btn--${size}`]: size,
      [`phxsg-btn--justify-${justify}`]: justify,
      [`phxsg-btn--${variant}`]: variant,
      'phxsg-btn--full': full,
      'phxsg-btn--loading': loading,
      'phxsg-btn--icon-only': iconOnly,
      'phxsg-btn--icon': startIcon.value || endIcon.value,
      'phxsg-btn--expand-target': expandTarget,
    },
  ];
});
</script>

<template>
  <component :is="use" v-bind="elementAttrs" :class="rootClasses">
    <slot name="start">
      <Transition name="phxsg-btn__icon-">
        <PhxsgLoader v-if="loading" v-bind="loaderProps" />
        <span v-else-if="startIcon" class="phxsg-btn__icon phxsg-btn__icon--start">
          <PhxsgIcon v-bind="startIcon" />
        </span>
      </Transition>
    </slot>

    <template v-if="hasDefaultSlot || label">
      <span :class="iconOnly ? 'sr-only' : 'phxsg-btn__text'">
        <slot>
          {{ label }}
        </slot>
      </span>
    </template>

    <slot name="content">
      <PhxsgIcon v-if="onlyIcon" v-bind="onlyIcon" />
    </slot>

    <slot name="end">
      <Transition name="phxsg-btn__icon-">
        <span v-if="endIcon" class="phxsg-btn__icon phxsg-btn__icon--end">
          <PhxsgIcon v-bind="endIcon" />
        </span>
      </Transition>
    </slot>
  </component>
</template>

<style src="./button.css" />
